// WP-UX-1 — the ONE design-token source for both apps (UX v1.6 §10, Theme B
// "Steier Workspace"; Build Plan Track U, UX-1 row). The field theme
// (apps/field/src/theme/theme.ts) and office-web (tokens.css + branding.ts)
// CONSUME this module — they never re-declare a value. Role names are the
// spec's, as written (`identity`, `on.identity`, `brand.accessible`, …), so a
// reviewer can read §10.1 and this file side by side.
//
// RULES
// - Every value here is a Theme B value exactly as the §10 tables state it.
//   Change the spec first, then this file, then let the contrast test tell you
//   whether the pairing still passes (contrast is COMPUTED by the test over
//   `VALIDATED_PAIRINGS`, never typed into an assertion).
// - Pure data + pure helpers. No DOM, no React Native, no Node built-ins —
//   this module is reachable from `@steier/shared/contract` and must bundle
//   in Vite (office), Metro/Hermes (field) and run under node:test.
// - Status roles and the stage-chip palette are FIXED: a tenant can never
//   re-colour them (only `identity` and `brand.*` pass through the colour
//   guard in ./colour-guard.ts).
// ---------------------------------------------------------------------------
// §10.1 colour roles
// ---------------------------------------------------------------------------
/**
 * §10.1 colour roles, keyed by the spec's role name (the `colour.` prefix
 * dropped). `hold`, `quote` and `cancelled` are text/surface PAIRS in the
 * spec, so they carry `.text` / `.surf` suffixes like `quote.text`/`quote.surf`.
 */
export const COLOUR_ROLES = {
    // Brand roles — tenant-themable through the guard ONLY.
    identity: "#1F3A5F",
    "on.identity": "#FFFFFF",
    "brand.accessible": "#1D5FD1",
    "brand.accessible.hover": "#174FB0",
    "brand.accessible.pressed": "#12408F",
    "brand.fill": "#3D7BE8",
    "brand.surface": "#E8F0FD",
    // Neutral roles.
    ink: "#181B21",
    "ink.muted": "#4A5260",
    surface: "#FFFFFF",
    "surface.sunken": "#F3F4F7",
    boundary: "#7A8494",
    divider: "#D3DAE3",
    // Status roles — fixed, never tenant-themable, never through the guard.
    success: "#047857",
    warning: "#B45309",
    danger: "#B91C1C",
    "quote.text": "#5B21B6",
    "quote.surf": "#EDE9FE",
    "hold.text": "#92400E",
    "hold.surf": "#FEF3C7",
    "cancelled.text": "#4B5563",
    "cancelled.surf": "#F3F4F6",
};
/** Every §10.1 role name — the list the app token tests iterate. */
export const COLOUR_ROLE_NAMES = Object.keys(COLOUR_ROLES);
/** The roles a tenant's O-15 colours may re-theme (through the guard). */
export const BRAND_COLOUR_ROLES = [
    "identity",
    "on.identity",
    "brand.accessible",
    "brand.accessible.hover",
    "brand.accessible.pressed",
    "brand.fill",
    "brand.surface",
];
/** The roles no tenant colour may ever touch (§10.1 "fixed"). */
export const STATUS_COLOUR_ROLES = [
    "success",
    "warning",
    "danger",
    "quote.text",
    "quote.surf",
    "hold.text",
    "hold.surf",
    "cancelled.text",
    "cancelled.surf",
];
/**
 * CSS custom-property name for a role: `brand.accessible.hover` →
 * `--colour-brand-accessible-hover`. office-web's tokens.css declares exactly
 * these; its token test derives the expected list from `COLOUR_ROLE_NAMES`.
 */
export function colourRoleCssVar(role) {
    return `--colour-${role.replace(/\./g, "-")}`;
}
/**
 * O-15 tenant accent presets (§10.1 "Tenant accent presets"). Teal is KEPT as
 * a preset (decision row 67 iii); the Steier default is action blue. Teal is
 * 4.48 on `surface.sunken`, so the guard darkens it one step there — which is
 * exactly why presets go through the guard like any custom colour.
 */
export const TENANT_ACCENT_PRESETS = [
    { id: "action-blue", hex: "#1D5FD1" },
    { id: "teal", hex: "#2E7D6B" },
];
/**
 * §10.3, keyed by the CANONICAL stage id (the API's JobStage enum / the
 * device `DEVICE_JOB_STAGES` values); no QUOTE stage (v1.7, row 97 — the
 * quote STATUS chip is §10.3.1 below). App chip tests iterate their runtime
 * stage arrays and look each one up here — a missing row fails, by construction.
 */
export const STAGE_CHIP_PALETTE = {
    NEW: chip("#E5E7EB", "#111827"),
    SCHEDULED: chip("#DBEAFE", "#1E40AF"),
    ON_SITE: chip("#CFFAFE", "#155E75"),
    IN_PROGRESS: chip("#DBEAFE", "#1D4ED8", { pulse: true }),
    COMPLETED: chip("#D1FAE5", "#065F46"),
    READY_FOR_INVOICING: chip("#FEF3C7", "#92400E"),
    INVOICED: chip("#047857", "#FFFFFF"),
    ON_HOLD: chip("#FEF3C7", "#92400E", { glyph: "⏸", dashedBorder: true }),
    CANCELLED: chip("#F3F4F6", "#4B5563", { glyph: "✕", strikethroughParent: true }),
};
/** Every chip stage id, in §10.3 table order. */
export const CHIP_STAGE_IDS = Object.keys(STAGE_CHIP_PALETTE);
/**
 * The chip ids the FIELD app may never render (audit F-05). Empty since UX v1.7
 * removed QUOTE from the stage set (row 97); kept as the guard's input so a
 * future office-only stage is caught by the existing app tests.
 */
export const OFFICE_ONLY_CHIP_STAGES = CHIP_STAGE_IDS.filter((id) => STAGE_CHIP_PALETTE[id].officeOnly);
function chip(bg, text, extras = {}) {
    return {
        bg,
        text,
        glyph: null,
        dashedBorder: false,
        pulse: false,
        strikethroughParent: false,
        officeOnly: false,
        ...extras,
    };
}
function quoteChip(bg, text, glyph, marker = false) {
    return { bg, text, glyph, marker };
}
/**
 * §10.3.1 — the ONE quote-status chip table, keyed by the office `QuoteStatus`
 * ids (the API's quote status enum) plus the `CHASE` marker. It reuses
 * palettes the system already has (no new colour): Issued is the §10.1
 * `quote.*` pair, Approved = Completed, Rejected = Cancelled, Expired = On
 * Hold; Draft is the neutral pair; `Chase due` is `colour.warning` text on
 * `surface`. office-web generates `.chip--<id>` from this table and its chip
 * test iterates the office runtime quote-status array against it.
 */
export const QUOTE_STATUS_CHIP_PALETTE = {
    DRAFT: quoteChip("#F3F4F6", "#374151", "◇"),
    ISSUED: quoteChip(COLOUR_ROLES["quote.surf"], COLOUR_ROLES["quote.text"], "✉"),
    APPROVED: quoteChip(STAGE_CHIP_PALETTE.COMPLETED.bg, STAGE_CHIP_PALETTE.COMPLETED.text, "✓"),
    REJECTED: quoteChip(STAGE_CHIP_PALETTE.CANCELLED.bg, STAGE_CHIP_PALETTE.CANCELLED.text, "✕"),
    EXPIRED: quoteChip(STAGE_CHIP_PALETTE.ON_HOLD.bg, STAGE_CHIP_PALETTE.ON_HOLD.text, "⌛"),
    CHASE: quoteChip(COLOUR_ROLES.surface, COLOUR_ROLES.warning, "⏳", true),
};
/** Every §10.3.1 row id (the five statuses, then the `CHASE` marker), in table order. */
export const QUOTE_CHIP_IDS = Object.keys(QUOTE_STATUS_CHIP_PALETTE);
/** The five quote STATUS ids — the table minus the marker. */
export const QUOTE_STATUS_CHIP_IDS = QUOTE_CHIP_IDS.filter((id) => !QUOTE_STATUS_CHIP_PALETTE[id].marker);
function type(size, line, weight = 400, extras = {}) {
    return { size, line, weight, tabular: false, uppercaseTracking: false, ...extras };
}
/** §10.2 type scale — `mobile` is the field column, `web` the office column. */
export const TYPE_SCALE = {
    mobile: {
        display: type(28, 34, 700),
        jobnumber: type(22, 28, 700, { tabular: true }),
        h2: type(20, 26, 600),
        h3: type(17, 24, 600, { uppercaseTracking: true }),
        body: type(17, 24),
        small: type(15, 20),
        caption: type(13, 16),
        button: type(17, 24, 600),
    },
    web: {
        display: type(30, 38, 700),
        jobnumber: type(20, 28, 700, { tabular: true }),
        h2: type(20, 28, 600),
        h3: type(14, 20, 600, { uppercaseTracking: true }),
        body: type(16, 24),
        small: type(14, 20),
        caption: type(12, 16),
        button: type(15, 20, 600),
    },
};
/** The platform system stack — no licensed brand font (§10.2). */
export const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
/** §10.2 spacing steps on the 4 px grid. */
export const SPACING = [4, 8, 12, 16, 24, 32, 48];
/** §10.2 layout spacing (mobile = field, web = office). */
export const LAYOUT_SPACING = {
    cardPadding: 16,
    cardGap: 16,
    gutter: { mobile: 16, web: 24 },
    pageHeaderPadding: { mobile: 16, web: 24 },
};
/** §10.2 radii (unchanged from v1.5). `chip` 999 = pill. */
export const RADII = {
    card: 12,
    button: 10,
    input: 8,
    chip: 999,
    thumb: 8,
};
/**
 * §10.2 elevation — DECORATION ONLY. Nothing may depend on a shadow being
 * visible (they vanish in sunlight and in high-contrast modes).
 */
export const ELEVATION = {
    flat: { shadow: "none", android: 0, hairline: COLOUR_ROLES.divider },
    rest: {
        shadow: "0 1px 2px rgba(24,27,33,.06), 0 1px 3px rgba(24,27,33,.10)",
        android: 1,
        hairline: null,
    },
    raised: {
        shadow: "0 1px 3px rgba(24,27,33,.08), 0 4px 12px rgba(24,27,33,.10)",
        android: 3,
        hairline: null,
    },
};
/**
 * §10.2 density axis — a token axis, not per-screen tweaks. `touch` is the
 * field app always; `comfortable` is the office default. `compact` is
 * RESERVED and deliberately NOT built (decision row 67 ii) — it is declared
 * by name only in `RESERVED_DENSITY_STEPS` so nobody mistakes its absence for
 * an oversight.
 */
export const DENSITY = {
    touch: {
        body: { size: 17, line: 24 },
        control: 56,
        minTarget: 48,
        row: 64,
        stepper: 48,
        checkInOut: 96,
    },
    comfortable: {
        body: { size: 16, line: 24 },
        control: 40,
        minTarget: 44,
        row: 48,
        stepper: null,
        checkInOut: null,
    },
};
/** Declared, not built (row 67 ii). */
export const RESERVED_DENSITY_STEPS = ["compact"];
/** §10.4 — identical on the field app (F-02/F-04/F-08) and office O-05. */
export const THUMBNAIL_STATES = {
    uploaded: { badge: COLOUR_ROLES.success, glyph: "✓", imageOpacity: 1, tapForDetail: false },
    pending: { badge: COLOUR_ROLES.warning, glyph: "⌛", imageOpacity: 0.6, tapForDetail: false },
    retrying: { badge: COLOUR_ROLES.warning, glyph: "⚠", imageOpacity: 1, tapForDetail: true },
};
/**
 * §10.4 tag-corner ribbons. The spec names the hues (BEFORE teal / AFTER
 * indigo) without hex values — these are the build's picks (D-WPUX-1-5), both
 * validated for white ribbon text in `VALIDATED_PAIRINGS`.
 */
export const THUMBNAIL_TAG_RIBBONS = {
    BEFORE: { bg: "#2E7D6B", text: "#FFFFFF" },
    AFTER: { bg: "#4338CA", text: "#FFFFFF" },
};
/** §10.5 — the four visual tiers, keyed by a stable id. */
export const AGEING_TIERS = {
    new: { minDays: 0, maxDays: 0, glyph: "●", text: COLOUR_ROLES["ink.muted"], bg: null, rowBorder: null },
    amberText: { minDays: 1, maxDays: 2, glyph: "⏳", text: COLOUR_ROLES.warning, bg: null, rowBorder: null },
    amberFill: { minDays: 3, maxDays: 6, glyph: "⏳", text: "#92400E", bg: "#FEF3C7", rowBorder: null },
    red: { minDays: 7, maxDays: null, glyph: "🔥", text: "#B91C1C", bg: "#FEE2E2", rowBorder: COLOUR_ROLES.danger },
};
/**
 * The office worklist DTO (`apps/office-web/src/features/invoicing/contracts.ts`
 * `AgeingTier = "new" | "amber" | "red"`, computed by the API per D-S3: amber
 * at 2 days, red at 7) emits THREE tiers. §10.5 draws four. This map is how
 * the three producer values pick a §10.5 style; the "1–2 days amber text" tier
 * has no producer today (finding F-UX1-1 in the WP-UX-1 worklog — a decision
 * for the lane, not something presentation may invent).
 */
export const AGEING_TIER_FOR_WORKLIST = {
    new: "new",
    amber: "amberFill",
    red: "red",
};
/**
 * §10.6 button variants. Primary is NEVER filled with `brand.fill` (it cannot
 * carry normal-size text). There is no destructive/delete variant (BR-002).
 */
export const BUTTON_VARIANTS = {
    primary: {
        fill: COLOUR_ROLES["brand.accessible"],
        text: "#FFFFFF",
        border: null,
        hoverFill: COLOUR_ROLES["brand.accessible.hover"],
        pressedFill: COLOUR_ROLES["brand.accessible.pressed"],
        underlineOnHover: false,
    },
    flagPrimary: {
        fill: COLOUR_ROLES.warning,
        text: "#FFFFFF",
        border: null,
        hoverFill: null,
        pressedFill: null,
        underlineOnHover: false,
    },
    secondary: {
        fill: COLOUR_ROLES.surface,
        text: COLOUR_ROLES["brand.accessible"],
        border: COLOUR_ROLES.boundary,
        hoverFill: null,
        pressedFill: null,
        underlineOnHover: false,
    },
    tertiary: {
        fill: null,
        text: COLOUR_ROLES["brand.accessible"],
        border: null,
        hoverFill: null,
        pressedFill: null,
        underlineOnHover: true,
    },
    onIdentity: {
        fill: null,
        text: COLOUR_ROLES["on.identity"],
        border: COLOUR_ROLES["on.identity"],
        hoverFill: null,
        pressedFill: null,
        underlineOnHover: false,
    },
};
/** §10.6 focus ring: `brand.accessible` on surfaces, `on.identity` on the shell. */
export const FOCUS_RING = {
    surfaces: { colour: COLOUR_ROLES["brand.accessible"], width: 2, offset: 2 },
    identity: { colour: COLOUR_ROLES["on.identity"], width: 2, offset: 2 },
};
/** §10.6 inputs: `boundary` is the only edge (never `divider`). */
export const INPUT_STYLE = {
    fill: COLOUR_ROLES.surface,
    border: COLOUR_ROLES.boundary,
    radius: RADII.input,
    height: { mobile: 48, web: 40 },
    label: COLOUR_ROLES["ink.muted"],
    error: COLOUR_ROLES.danger,
};
/** §10.6 motion: 150 ms ease-out; 0 under reduced motion. */
export const MOTION = { durationMs: 150, easing: "ease-out", reducedMotionMs: 0 };
const WHITE = COLOUR_ROLES.surface;
const SUNKEN = COLOUR_ROLES["surface.sunken"];
function pair(fg, bg, min, stated, note) {
    return { fg, bg, min, stated, note };
}
/** §10.3 chip rows as pairings — derived from the palette, not re-typed. */
const CHIP_STATED = {
    NEW: 14.33,
    SCHEDULED: 7.15,
    ON_SITE: 6.49,
    IN_PROGRESS: 5.49,
    COMPLETED: 6.78,
    READY_FOR_INVOICING: 6.37,
    INVOICED: 5.48,
    ON_HOLD: 6.37,
    CANCELLED: 6.87,
};
const CHIP_PAIRINGS = CHIP_STAGE_IDS.map((id) => pair(STAGE_CHIP_PALETTE[id].text, STAGE_CHIP_PALETTE[id].bg, 4.5, CHIP_STATED[id], `§10.3 chip ${id}`));
/** §10.3.1 quote-status rows as pairings — derived from the table, ratios as stated. */
const QUOTE_CHIP_STATED = {
    DRAFT: 9.37,
    ISSUED: 7.57,
    APPROVED: 6.78,
    REJECTED: 6.87,
    EXPIRED: 6.37,
    CHASE: 5.02,
};
const QUOTE_CHIP_PAIRINGS = QUOTE_CHIP_IDS.map((id) => pair(QUOTE_STATUS_CHIP_PALETTE[id].text, QUOTE_STATUS_CHIP_PALETTE[id].bg, 4.5, QUOTE_CHIP_STATED[id], `§10.3.1 quote-status chip ${id}`));
/** §10.5 filled tiers as pairings — derived from the tier table. */
const AGEING_PAIRINGS = [
    pair(AGEING_TIERS.amberFill.text, AGEING_TIERS.amberFill.bg, 4.5, 6.37, "§10.5 3–6 days amber fill"),
    pair(AGEING_TIERS.red.text, AGEING_TIERS.red.bg, 4.5, 5.3, "§10.5 ≥7 days red fill"),
    pair(AGEING_TIERS.amberText.text, WHITE, 4.5, 5.02, "§10.5 1–2 days amber text on surface"),
    pair(AGEING_TIERS.new.text, WHITE, 4.5, 7.87, "§10.5 <1 day grey text on surface"),
];
/**
 * Every pairing §10 validates, with the ratio the spec states. The shared
 * contrast test iterates THIS list (never a hand list in the test), computes
 * the WCAG 2.x ratio for each and asserts both `>= min` and `≈ stated`.
 * Decorative pairings with no target (`divider` on `surface`, 1.41) are not
 * listed because they carry no requirement.
 */
export const VALIDATED_PAIRINGS = [
    // Brand roles
    pair(COLOUR_ROLES["on.identity"], COLOUR_ROLES.identity, 4.5, 11.48, "§10.1 on.identity on identity"),
    pair(COLOUR_ROLES["brand.accessible"], WHITE, 4.5, 5.82, "§10.1 brand.accessible text on surface"),
    pair(COLOUR_ROLES["brand.accessible"], SUNKEN, 4.5, 5.29, "§10.1 brand.accessible text on surface.sunken"),
    pair(COLOUR_ROLES["brand.accessible"], COLOUR_ROLES["brand.surface"], 4.5, 5.07, "§10.1 brand.accessible on brand.surface"),
    pair(WHITE, COLOUR_ROLES["brand.accessible"], 4.5, 5.82, "§10.6 white on primary button"),
    pair(WHITE, COLOUR_ROLES["brand.accessible.hover"], 4.5, 7.55, "§10.1 white on hover"),
    pair(WHITE, COLOUR_ROLES["brand.accessible.pressed"], 4.5, 9.74, "§10.1 white on pressed"),
    pair(COLOUR_ROLES["brand.fill"], WHITE, 3, 4.04, "§10.1 brand.fill as a UI boundary on surface (≥ 3)"),
    pair(WHITE, COLOUR_ROLES["brand.fill"], 3, 4.04, "§10.1 white LARGE text on brand.fill (≥ 3)"),
    pair(COLOUR_ROLES.ink, COLOUR_ROLES["brand.surface"], 4.5, 15.04, "§10.1 ink on brand.surface"),
    // Neutral roles
    pair(COLOUR_ROLES.ink, WHITE, 4.5, 17.25, "§10.1 ink on surface"),
    pair(COLOUR_ROLES.ink, SUNKEN, 4.5, 15.68, "§10.1 ink on surface.sunken"),
    pair(COLOUR_ROLES["ink.muted"], WHITE, 4.5, 7.87, "§10.1 ink.muted on surface"),
    pair(COLOUR_ROLES["ink.muted"], SUNKEN, 4.5, 7.16, "§10.1 ink.muted on surface.sunken"),
    pair(COLOUR_ROLES.identity, SUNKEN, 3, 10.44, "§10.1 identity shell edge against surface.sunken"),
    pair(COLOUR_ROLES.boundary, WHITE, 3, 3.78, "§10.1 boundary on surface (WCAG 1.4.11)"),
    pair(COLOUR_ROLES.boundary, SUNKEN, 3, 3.44, "§10.1 boundary on surface.sunken (WCAG 1.4.11)"),
    // Status roles
    pair(COLOUR_ROLES.success, WHITE, 4.5, 5.48, "§10.1 success on surface"),
    pair(COLOUR_ROLES.warning, WHITE, 4.5, 5.02, "§10.1 warning text on surface"),
    pair(COLOUR_ROLES.warning, SUNKEN, 4.5, 4.57, "§10.1 warning text on surface.sunken"),
    pair(WHITE, COLOUR_ROLES.warning, 4.5, 5.02, "§10.6 white on flag-primary (warning)"),
    pair(COLOUR_ROLES.danger, WHITE, 4.5, 6.47, "§10.1 danger on surface"),
    pair(COLOUR_ROLES["quote.text"], COLOUR_ROLES["quote.surf"], 4.5, 7.57, "§10.1 quote chip"),
    pair(COLOUR_ROLES["hold.text"], COLOUR_ROLES["hold.surf"], 4.5, 6.37, "§10.1 hold chip"),
    pair(COLOUR_ROLES["cancelled.text"], COLOUR_ROLES["cancelled.surf"], 4.5, 6.87, "§10.1 cancelled chip"),
    // Tenant accent preset (teal) — on surface only; on surface.sunken it is 4.48
    // and the guard darkens it (asserted in the guard test, not here).
    pair("#2E7D6B", WHITE, 4.5, 4.93, "§10.1 teal preset as text on surface"),
    pair(WHITE, "#2E7D6B", 4.5, 4.93, "§10.1 white on teal preset"),
    // §10.4 ribbons (build-chosen hexes, D-WPUX-1-5)
    pair(THUMBNAIL_TAG_RIBBONS.BEFORE.text, THUMBNAIL_TAG_RIBBONS.BEFORE.bg, 4.5, 4.93, "§10.4 BEFORE ribbon"),
    pair(THUMBNAIL_TAG_RIBBONS.AFTER.text, THUMBNAIL_TAG_RIBBONS.AFTER.bg, 4.5, 7.9, "§10.4 AFTER ribbon"),
    ...CHIP_PAIRINGS,
    ...QUOTE_CHIP_PAIRINGS,
    ...AGEING_PAIRINGS,
];
/**
 * The §10 token tree in one object, for consumers that want a single import
 * (the office tokens.css generator / drift test, the field theme).
 */
export const DESIGN_TOKENS = {
    colour: COLOUR_ROLES,
    stageChip: STAGE_CHIP_PALETTE,
    quoteStatusChip: QUOTE_STATUS_CHIP_PALETTE,
    type: TYPE_SCALE,
    fontStack: FONT_STACK,
    spacing: SPACING,
    layout: LAYOUT_SPACING,
    radii: RADII,
    elevation: ELEVATION,
    density: DENSITY,
    reservedDensity: RESERVED_DENSITY_STEPS,
    thumbnail: THUMBNAIL_STATES,
    thumbnailRibbons: THUMBNAIL_TAG_RIBBONS,
    ageing: AGEING_TIERS,
    button: BUTTON_VARIANTS,
    focusRing: FOCUS_RING,
    input: INPUT_STYLE,
    motion: MOTION,
    accentPresets: TENANT_ACCENT_PRESETS,
};
//# sourceMappingURL=design-tokens.js.map